// SPDX-License-Identifier: GPL-3.0-or-later
// The WebAudio engine. It owns the one audio context and the gain buses. Sound effects and music
// plug into the buses. The engine makes no sound itself.
//
// Graph: sfx bus and music bus -> master -> limiter -> destination.
//
// - The context starts on the first `unlock()`. The page calls it from a user gesture.
// - A browser without WebAudio gives a null context. Then every call here does nothing.
// - A gain change ramps over a few milliseconds. A ramp makes no click.
// - `duck()` dips the music bus only. The sfx bus stays at its level.
// - The engine has no timer. It reads the time of the audio context.

/** The part of the browser audio context that the engine uses. */
export type AudioContextLike = AudioContext;

/** What the engine needs from the page. */
export interface AudioEngineDeps {
  /** Make the context. Return null when the browser has no WebAudio. */
  createContext: () => AudioContextLike | null;
}

/** The audio engine. */
export interface AudioEngine {
  /** True after the context exists. */
  readonly ready: boolean;
  /** True when the sound effects are on. It is the setting, not the context state. */
  readonly sfxEnabled: boolean;
  /** The context, or null before `unlock()` or without WebAudio. */
  context(): BaseAudioContext | null;
  /** The gain bus of the sound effects. Null before the context exists. */
  sfxBus(): GainNode | null;
  /** The gain bus of the music. Null before the context exists. */
  musicBus(): GainNode | null;
  /** Make the context and start it. Call it from a user gesture. Safe to call again. */
  unlock(): Promise<void>;
  /** Turn the sound effects on or off. The volume is 0 to 1. */
  setSfx(enabled: boolean, volume: number): void;
  /** Turn the music on or off. The volume is 0 to 1. */
  setMusic(enabled: boolean, volume: number): void;
  /** Dip the music bus. `depth` is 0 to 1 (1 is silence). The music comes back after `ms`. */
  duck(depth: number, ms: number): void;
  /** Pause the whole audio (game pause, hidden tab). */
  suspend(): void;
  /** Undo `suspend()`. */
  resume(): void;
  /** Call `cb` when the context exists. It runs at once when the context exists now. */
  onReady(cb: (ctx: BaseAudioContext) => void): () => void;
}

/** Seconds that a gain change takes. */
export const GAIN_RAMP_SECONDS = 0.03;
/** Seconds that the music takes to come back after a duck. */
export const DUCK_RELEASE_SECONDS = 0.35;
/** The master level. It leaves head room for the limiter. */
export const MASTER_LEVEL = 0.9;
/** The default volume before the page sets one. */
const DEFAULT_LEVEL = 0.6;

function clamp01(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

function setNow(param: AudioParam, value: number): void {
  param.value = value;
}

function makeLimiter(ctx: AudioContextLike): DynamicsCompressorNode {
  const limiter = ctx.createDynamicsCompressor();
  setNow(limiter.threshold, -10);
  setNow(limiter.knee, 8);
  setNow(limiter.ratio, 12);
  setNow(limiter.attack, 0.003);
  setNow(limiter.release, 0.2);
  return limiter;
}

interface Graph {
  readonly ctx: AudioContextLike;
  readonly sfx: GainNode;
  readonly music: GainNode;
}

function buildGraph(ctx: AudioContextLike): Graph {
  const master = ctx.createGain();
  setNow(master.gain, MASTER_LEVEL);
  const limiter = makeLimiter(ctx);
  master.connect(limiter);
  limiter.connect(ctx.destination);
  const sfx = ctx.createGain();
  const music = ctx.createGain();
  sfx.connect(master);
  music.connect(master);
  return { ctx, sfx, music };
}

/** Ramp one gain param to a level. The ramp starts at the value that the param has now. */
function rampTo(param: AudioParam, level: number, now: number): void {
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(level, now + GAIN_RAMP_SECONDS);
}

/** The mutable state of one engine. */
interface EngineState {
  graph: Graph | null;
  paused: boolean;
  sfxOn: boolean;
  sfxLevel: number;
  musicOn: boolean;
  musicLevel: number;
  duckDepth: number;
  duckUntil: number;
  readonly readyCallbacks: Set<(ctx: BaseAudioContext) => void>;
}

function audioEngineApplySfx(st: EngineState): void {
  if (!st.graph) return;
  rampTo(st.graph.sfx.gain, st.sfxOn ? st.sfxLevel : 0, st.graph.ctx.currentTime);
}

function audioEngineApplyMusic(st: EngineState): void {
  if (!st.graph) return;
  const { ctx, music } = st.graph;
  const now = ctx.currentTime;
  const base = st.musicOn ? st.musicLevel : 0;
  const param = music.gain;
  if (st.duckUntil <= now) {
    rampTo(param, base, now);
    return;
  }
  const low = base * (1 - st.duckDepth);
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(low, now + GAIN_RAMP_SECONDS);
  param.setValueAtTime(low, st.duckUntil);
  param.linearRampToValueAtTime(base, st.duckUntil + DUCK_RELEASE_SECONDS);
}

function audioEngineMakeGraph(deps: AudioEngineDeps): Graph | null {
  let ctx: AudioContextLike | null = null;
  try {
    ctx = deps.createContext();
  } catch {
    ctx = null;
  }
  return ctx ? buildGraph(ctx) : null;
}

function audioEngineAnnounce(st: EngineState, ctx: BaseAudioContext): void {
  for (const cb of [...st.readyCallbacks]) {
    try {
      cb(ctx);
    } catch {
      // A bad callback must not stop the others.
    }
  }
  st.readyCallbacks.clear();
}

async function audioEngineSafeResume(ctx: AudioContextLike): Promise<void> {
  try {
    await ctx.resume();
  } catch {
    // The browser refused. The next gesture tries again.
  }
}

async function audioEngineUnlock(st: EngineState, deps: AudioEngineDeps): Promise<void> {
  if (!st.graph) {
    st.graph = audioEngineMakeGraph(deps);
    if (!st.graph) return;
    setNow(st.graph.sfx.gain, st.sfxOn ? st.sfxLevel : 0);
    setNow(st.graph.music.gain, st.musicOn ? st.musicLevel : 0);
    audioEngineAnnounce(st, st.graph.ctx);
  }
  if (!st.paused && st.graph.ctx.state !== "running") await audioEngineSafeResume(st.graph.ctx);
}

function audioEngineDuck(st: EngineState, depth: number, ms: number): void {
  if (!st.graph || !Number.isFinite(ms) || ms <= 0) return;
  const now = st.graph.ctx.currentTime;
  const d = clamp01(depth, 0);
  st.duckDepth = st.duckUntil > now ? Math.max(st.duckDepth, d) : d;
  st.duckUntil = Math.max(st.duckUntil, now + ms / 1000);
  audioEngineApplyMusic(st);
}

function audioEngineSuspend(st: EngineState): void {
  st.paused = true;
  if (!st.graph) return;
  st.graph.ctx.suspend().catch(() => undefined);
}

function audioEngineResume(st: EngineState): void {
  st.paused = false;
  if (!st.graph) return;
  st.graph.ctx.resume().catch(() => undefined);
}

function audioEngineOnReady(st: EngineState, cb: (ctx: BaseAudioContext) => void): () => void {
  if (st.graph) {
    cb(st.graph.ctx);
    return () => undefined;
  }
  st.readyCallbacks.add(cb);
  return () => {
    st.readyCallbacks.delete(cb);
  };
}

/** Make the audio engine. */
export function createAudioEngine(deps: AudioEngineDeps): AudioEngine {
  const st: EngineState = {
    graph: null,
    paused: false,
    sfxOn: true,
    sfxLevel: DEFAULT_LEVEL,
    musicOn: true,
    musicLevel: DEFAULT_LEVEL,
    duckDepth: 0,
    duckUntil: 0,
    readyCallbacks: new Set(),
  };
  return {
    get ready() {
      return st.graph !== null;
    },
    get sfxEnabled() {
      return st.sfxOn;
    },
    context: () => st.graph?.ctx ?? null,
    sfxBus: () => st.graph?.sfx ?? null,
    musicBus: () => st.graph?.music ?? null,
    unlock: () => audioEngineUnlock(st, deps),
    setSfx(enabled, volume) {
      st.sfxOn = enabled;
      st.sfxLevel = clamp01(volume, st.sfxLevel);
      audioEngineApplySfx(st);
    },
    setMusic(enabled, volume) {
      st.musicOn = enabled;
      st.musicLevel = clamp01(volume, st.musicLevel);
      audioEngineApplyMusic(st);
    },
    duck: (depth, ms) => audioEngineDuck(st, depth, ms),
    suspend: () => audioEngineSuspend(st),
    resume: () => audioEngineResume(st),
    onReady: (cb) => audioEngineOnReady(st, cb),
  };
}
